import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency, cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  User,
  Phone,
  Mail,
  Wallet,
  CreditCard,
  PiggyBank,
  AlertTriangle,
  ArrowLeft,
  Printer,
  RefreshCw,
  ArrowDownLeft,
  ArrowUpRight,
  CircleDollarSign,
  FileText,
} from "lucide-react";

interface MemberProfile {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  memberNumber: number;
}

interface StatementSummary {
  totalSavings: number;
  totalLoans: number;
  totalRepaid: number;
  outstandingBalance: number;
  penalties: number;
}

interface Transaction {
  id: string;
  date: string;
  description: string;
  type: "savings" | "loan" | "repayment" | "penalty";
  debit: number;
  credit: number;
  balance: number;
}

interface StatementData {
  member: MemberProfile;
  summary: StatementSummary;
  transactions: Transaction[];
}

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    blue: "bg-blue-50",
    red: "bg-red-50",
    amber: "bg-amber-50",
    purple: "bg-purple-50",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    blue: "text-blue-600",
    red: "text-red-600",
    amber: "text-amber-600",
    purple: "text-purple-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {typeof value === "number" ? formatCurrency(value) : value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const typeConfig: Record<
  string,
  { label: string; icon: React.ReactNode; bg: string; text: string }
> = {
  savings: {
    label: "Savings",
    icon: <PiggyBank className="h-3 w-3" />,
    bg: "bg-[#009A44]/10",
    text: "text-[#009A44]",
  },
  loan: {
    label: "Loan",
    icon: <CreditCard className="h-3 w-3" />,
    bg: "bg-blue-50",
    text: "text-blue-600",
  },
  repayment: {
    label: "Repayment",
    icon: <ArrowDownLeft className="h-3 w-3" />,
    bg: "bg-purple-50",
    text: "text-purple-600",
  },
  penalty: {
    label: "Penalty",
    icon: <AlertTriangle className="h-3 w-3" />,
    bg: "bg-red-50",
    text: "text-red-600",
  },
};

export default function MemberStatement() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["member-statement", memberId],
    async () => {
      const { data } = await api.get(`/reports/member/${memberId}/statement`);
      return data?.data as StatementData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
      enabled: !!memberId,
    }
  );

  const loading = status === "loading";
  const member = rawData?.member;
  const summary = rawData?.summary || {
    totalSavings: 0,
    totalLoans: 0,
    totalRepaid: 0,
    outstandingBalance: 0,
    penalties: 0,
  };
  const transactions = rawData?.transactions || [];

  const handlePrint = () => {
    window.print();
  };

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Member Statement
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Individual member financial statement
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load member statement
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => navigate("/reports/members")}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Members
                </button>
                <button
                  onClick={() => refetch()}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <RefreshCw className="h-4 w-4" /> Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden print:hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Member Statement
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      {loading
                        ? "Loading..."
                        : `${member?.firstName} ${member?.lastName} — Statement of Account`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => navigate("/reports/members")}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-[#009A44] hover:bg-white/90"
                >
                  <Printer className="h-4 w-4" />
                  Print Statement
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* Member Profile Header (visible on screen) */}
        <Card className="print:hidden">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#009A44]/10 shrink-0">
                <User className="h-7 w-7 text-[#009A44]" />
              </div>
              <div className="flex-1 min-w-0">
                {loading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-64" />
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-bold text-gray-900">
                      {member?.firstName} {member?.lastName}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <span className="font-medium">ID:</span>
                        {String(member?.memberNumber || "").padStart(4, "0")}
                      </span>
                      {member?.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" />
                          {member.phone}
                        </span>
                      )}
                      {member?.email && (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" />
                          {member.email}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="text-sm text-gray-500 shrink-0">
                Generated: {format(new Date(), "dd MMM yyyy")}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Print-only header */}
        <div className="hidden print:block mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Member Statement</h1>
          <div className="mt-2 text-sm text-gray-600">
            <p className="font-semibold text-base text-gray-800">
              {member?.firstName} {member?.lastName}
            </p>
            <div className="flex gap-4 mt-1">
              <span>ID: {String(member?.memberNumber || "").padStart(4, "0")}</span>
              {member?.phone && <span>Phone: {member.phone}</span>}
              {member?.email && <span>Email: {member.email}</span>}
            </div>
            <p className="mt-1">Date: {format(new Date(), "dd MMMM yyyy")}</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
          <SummaryCard
            title="Total Savings"
            value={summary.totalSavings}
            icon={<PiggyBank className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Total Loans"
            value={summary.totalLoans}
            icon={<CreditCard className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Total Repaid"
            value={summary.totalRepaid}
            icon={<ArrowDownLeft className="h-5 w-5" />}
            accentColor="purple"
            loading={loading}
          />
          <SummaryCard
            title="Outstanding"
            value={summary.outstandingBalance}
            icon={<CircleDollarSign className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
          <SummaryCard
            title="Penalties"
            value={summary.penalties}
            icon={<AlertTriangle className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
        </div>

        {/* Transaction Ledger */}
        <Card>
          <CardHeader className="print:hidden">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Transaction Ledger</CardTitle>
              <span className="text-xs text-gray-500">
                {loading ? "" : `${transactions.length} transaction(s)`}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="py-12 text-center">
                <Wallet className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No transactions found for this member</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">Date</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Description</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500">Type</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Debit</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Credit</th>
                      <th className="pb-2 font-medium text-gray-500 text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => {
                      const config = typeConfig[tx.type] || typeConfig.savings;
                      return (
                        <tr
                          key={tx.id}
                          className="border-b last:border-b-0 hover:bg-gray-50/50"
                        >
                          <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                            {format(new Date(tx.date), "dd MMM yyyy")}
                          </td>
                          <td className="py-3 pr-4 font-medium text-gray-900">
                            {tx.description}
                          </td>
                          <td className="py-3 pr-4">
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                                config.bg,
                                config.text
                              )}
                            >
                              {config.icon}
                              {config.label}
                            </span>
                          </td>
                          <td
                            className={cn(
                              "py-3 pr-4 text-right font-semibold whitespace-nowrap",
                              tx.debit > 0 ? "text-red-600" : "text-gray-400"
                            )}
                          >
                            {tx.debit > 0 ? formatCurrency(tx.debit) : "—"}
                          </td>
                          <td
                            className={cn(
                              "py-3 pr-4 text-right font-semibold whitespace-nowrap",
                              tx.credit > 0 ? "text-[#009A44]" : "text-gray-400"
                            )}
                          >
                            {tx.credit > 0 ? formatCurrency(tx.credit) : "—"}
                          </td>
                          <td className="py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                            {formatCurrency(tx.balance)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Print-friendly styles */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          table {
            page-break-inside: avoid;
          }
          tr {
            page-break-inside: avoid;
          }
          thead {
            display: table-header-group;
          }
        }
      `}</style>
    </div>
  );
}
